import { Component, DestroyRef, ElementRef, OnInit, ViewChild, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Clipboard } from '@angular/cdk/clipboard';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastService } from '../../services/toast.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { MatModule } from '../../mat/mat.module';
import { TwoFactorAppSetup, TwoFactorRecoveryCodes } from '../../models/twoFactor';
import { DownloadService } from '../../services/download.service';
import { PlatformService } from '../../services/platform.service';
import { codeDigits, tooManyAttempts } from '../../helpers/sign-in-wait';

export interface TwoFactorSetupDialogData<T extends TwoFactorRecoveryCodes = TwoFactorRecoveryCodes> {
  // The username, in the file of the codes.
  account: string;
  // Absent with codes: only the new recovery codes, made again from the profile.
  setup?: TwoFactorAppSetup;
  confirm?: (code: string) => Observable<T>;
  codes?: string[];
}

type SetupStep = 'scan' | 'confirm' | 'codes';

const CODES_FILE: string = 'UrbanHub-recovery-codes.txt';

// The authenticator app in three steps: scan the QR code (or type the key),
// confirm with its first code, save the recovery codes. The same from the
// profile and from the sign-in, which tells it how to confirm. Closes with the
// answer of the confirmation once the codes are saved, with false once the
// setup is over on the server, with nothing when left before; the codes cannot
// be left unsaved.
@Component({
  selector: 'urban-two-factor-setup-dialog',
  imports: [MatModule, ReactiveFormsModule, TranslateModule],
  providers: [DatePipe],
  templateUrl: './two-factor-setup-dialog.component.html',
  styleUrls: ['./two-factor-setup-dialog.component.scss']
})
export class TwoFactorSetupDialogComponent implements OnInit {
  protected readonly data: TwoFactorSetupDialogData = inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<TwoFactorSetupDialogComponent>);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly clipboard = inject(Clipboard);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly download = inject(DownloadService);
  private readonly datePipe = inject(DatePipe);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platform = inject(PlatformService);

  protected readonly steps: readonly SetupStep[] = ['scan', 'confirm', 'codes'];
  protected readonly step = signal<SetupStep>(this.data.setup ? 'scan' : 'codes');
  protected readonly qrCode = signal<string | null>(null);
  // Only where the app can be on the same device: a desktop has nothing to open it.
  protected readonly onPhone: boolean = this.platform.isNative || this.platform.isMobileViewport();
  protected readonly addToAppLink: SafeUrl | null = TwoFactorSetupDialogComponent.appLink(this.data.setup?.Uri, this.sanitizer);
  protected readonly keyGroups: string = (this.data.setup?.Secret ?? '').replace(/(.{4})(?=.)/g, '$1 ');

  protected readonly code = new FormControl<string>('', { nonNullable: true });
  protected readonly saved = new FormControl<boolean>(false, { nonNullable: true });
  protected error: { key: string, params?: Record<string, string | number> } | null = null;
  // The setup is over on the server: only closing is left.
  protected expired: boolean = false;
  protected confirming: boolean = false;
  protected codes: string[] = this.data.codes ?? [];
  private answer: TwoFactorRecoveryCodes | null = null;

  @ViewChild('codeInput') set codeInput(input: ElementRef<HTMLInputElement> | undefined) {
    if (input) {
      setTimeout(() => input.nativeElement.focus());
    }
  }

  ngOnInit(): void {
    if (!this.data.setup) {
      this.dialogRef.disableClose = true;
      return;
    }

    // Loaded here: the login page imports this dialog, and the bundle it
    // starts with need not carry the QR encoder for the few who set up the app.
    // A CommonJS package: the build hands its functions only under default
    // (ng serve by name too, which hid it).
    import('qrcode')
      .then(({ default: qr }) => qr.toDataURL(this.data.setup!.Uri, { margin: 1, width: 400, errorCorrectionLevel: 'M' }))
      .then(url => this.qrCode.set(url))
      .catch(() => this.qrCode.set(null));
  }

  protected copyKey(): void {
    this.copy(this.data.setup?.Secret ?? '');
  }

  protected copyCodes(): void {
    this.copy(this.codes.join('\n'));
  }

  protected saveCodes(): void {
    const date: string = this.datePipe.transform(Date.now(), 'dd/MM/yyyy HH:mm') ?? '';
    const text: string = [
      `UrbanHub - ${this.translate.instant('TWO_FACTOR.CODES_FILE_TITLE')}`,
      `${this.data.account} - ${date}`,
      '',
      ...this.codes,
      '',
      this.translate.instant('TWO_FACTOR.CODES_FILE_HINT'),
      ''
    ].join('\r\n');

    void this.download.save(new Blob([text], { type: 'text/plain;charset=utf-8' }), CODES_FILE);
  }

  protected normalizeCode(): void {
    const digits: string = codeDigits(this.code.value);

    if (digits !== this.code.value) {
      this.code.setValue(digits);
    }

    this.error = null;
  }

  protected confirmCode(): void {
    if (this.confirming || this.expired || !this.data.confirm) {
      return;
    }

    if (!/^\d{6}$/.test(this.code.value)) {
      this.error = { key: 'LOGIN.CODE_FORMAT' };
      return;
    }

    this.error = null;
    this.confirming = true;

    this.data.confirm(this.code.value)
      .pipe(
        finalize(() => this.confirming = false),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: answer => {
          this.answer = answer;
          this.codes = answer.RecoveryCodes ?? [];
          // The app is in use from now on: the codes are shown this once.
          this.dialogRef.disableClose = true;
          this.step.set('codes');
        },
        error: (err: HttpErrorResponse) => this.showServerError(err)
      });
  }

  protected finish(): void {
    if (this.saved.value) {
      void this.download.forget(CODES_FILE);
      this.dialogRef.close(this.answer ?? true);
    }
  }

  private copy(text: string): void {
    this.clipboard.copy(text);
    this.toast.done('GENERAL.COPIED_TO_CLIPBOARD');
  }

  // A wrong code keeps the setup while attempts are left; the last one, like an
  // expired setup, ends it.
  private showServerError(err: HttpErrorResponse): void {
    const message: unknown = err?.error?.Message;
    const attemptsLeft: number = Number(err?.error?.AttemptsLeft);

    if (err?.status === 401 && message === 'Invalid code' && attemptsLeft > 0) {
      this.error = { key: 'LOGIN.CODE_WRONG', params: { left: attemptsLeft } };
    }
    else if (err?.status === 401 && (message === 'Invalid code' || message === 'Code expired')) {
      this.expired = true;
      this.error = { key: 'TWO_FACTOR.SETUP_EXPIRED' };
    }
    else if (err?.status === 409 && message === 'Authenticator app in use') {
      this.expired = true;
      this.error = { key: 'TWO_FACTOR.APP_ALREADY' };
    }
    else if (err?.status === 429) {
      this.error = tooManyAttempts(err);
    }
    else {
      this.error = { key: err?.status === 0 ? 'ERROR.OFFLINE' : 'ERROR.SOMETHING_WRONG' };
    }
  }

  // Angular marks an otpauth: link unsafe; this one is the server's and opens
  // nothing but the authenticator app.
  private static appLink(uri: string | undefined, sanitizer: DomSanitizer): SafeUrl | null {
    return uri?.startsWith('otpauth://totp/') ? sanitizer.bypassSecurityTrustUrl(uri) : null;
  }
}
